import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Wrench, AlertTriangle, ExternalLink, History, Search, Package, Edit, BarChart3 } from "lucide-react";
import { fmtRoDate } from "../../lib/dateFmt";
import ItemDialog from "../inventar/ItemDialog";
import { toast } from "sonner";

/**
 * CarPartsAndHistoryPane — tab dedicat „🔧 Piese & Consumabile" în CarDetail.
 * - Tabel piese pe stoc cu KPI cards, categorie, furnizor, preț, status badges
 * - Buton „Adaugă piesă" cu ItemDialog (pre-fill linked_car_id)
 * - Click pe rând → edit
 * - Timeline reparații / revizii cu piesele consumate per intervenție
 *
 * Props: car (full car object), canEdit
 */
export default function CarPartsAndHistoryPane({ car, canEdit, onChanged }) {
  const navigate = useNavigate();
  const [parts, setParts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialog, setDialog] = useState(null); // null | { mode, item?, prefill? }
  const [meta, setMeta] = useState(null);
  const [cars, setCars] = useState([]);
  const [consumption, setConsumption] = useState(null); // { months, categories, total_events }
  const [consumptionMonths, setConsumptionMonths] = useState(6);

  const load = async () => {
    setLoading(true);
    try {
      const [partsRes, histRes, metaRes, carsRes, consRes] = await Promise.all([
        api.get(`/cars/${car.id}/spare-parts`),
        api.get(`/cars/${car.id}/parts-history`).catch(() => ({ data: [] })),
        api.get("/inventory/meta").catch(() => ({ data: null })),
        api.get("/cars").catch(() => ({ data: [] })),
        api.get(`/cars/${car.id}/consumption-report?months=${consumptionMonths}`).catch(() => ({ data: null })),
      ]);
      setParts(partsRes.data || []);
      setHistory(histRes.data || []);
      setMeta(metaRes.data);
      setCars(carsRes.data || []);
      setConsumption(consRes.data);
    } catch (e) {
      toast.error("Eroare la încărcarea pieselor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (car?.id) load(); }, [car?.id, consumptionMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return parts.filter((p) => {
      if (statusFilter !== "all") {
        const low = (Number(p.quantity) || 0) <= 1;
        if (statusFilter === "low" && !low) return false;
        if (statusFilter === "defect" && p.status !== "defect") return false;
        if (statusFilter === "ok" && (low || p.status === "defect")) return false;
      }
      if (!q) return true;
      return `${p.name || ""} ${p.serial || ""} ${p.supplier || ""} ${p.equipment_category || ""}`.toLowerCase().includes(q);
    });
  }, [parts, search, statusFilter]);

  const kpi = useMemo(() => {
    const totalQty = parts.reduce((s, p) => s + (Number(p.quantity) || 0), 0);
    const low = parts.filter((p) => (Number(p.quantity) || 0) <= 1).length;
    const defect = parts.filter((p) => p.status === "defect").length;
    const totalValue = parts.reduce((s, p) => s + ((Number(p.quantity) || 0) * (Number(p.price_chf) || 0)), 0);
    return { count: parts.length, totalQty, low, defect, totalValue };
  }, [parts]);

  const histKpi = useMemo(() => {
    const totalItems = history.reduce((s, h) => s + (h.total_items || 0), 0);
    const lastDate = history.length > 0 ? history[0].date : null;
    return { interventions: history.length, totalItems, lastDate };
  }, [history]);

  return (
    <div className="space-y-4" data-testid="car-parts-history-pane">
      {/* KPI cards — Stoc */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiCard icon={<Wrench size={14}/>} label="Piese pe stoc" value={kpi.count} sub={`Σ ${kpi.totalQty.toFixed(0)} buc`} color="emerald" testid="car-parts-kpi-count"/>
        <KpiCard icon={<Package size={14}/>} label="Valoare stoc" value={`${kpi.totalValue.toFixed(2)}`} sub="CHF" color="blue" testid="car-parts-kpi-value"/>
        <KpiCard icon={<AlertTriangle size={14}/>} label="Stoc scăzut" value={kpi.low} color="amber" testid="car-parts-kpi-low"/>
        <KpiCard icon={<AlertTriangle size={14}/>} label="Defecte" value={kpi.defect} color="red" testid="car-parts-kpi-defect"/>
        <KpiCard icon={<History size={14}/>} label="Intervenții" value={histKpi.interventions} sub={histKpi.lastDate ? `ultima: ${fmtRoDate(histKpi.lastDate)}` : "—"} color="zinc" testid="car-parts-kpi-interv"/>
      </div>

      {/* Raport consum lunar */}
      <ConsumptionReportCard
        consumption={consumption}
        months={consumptionMonths}
        onMonthsChange={setConsumptionMonths}
      />

      {/* Filters + actions */}
      <div className="card-flat p-3 flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută piesă, serie, furnizor..."
            className="pl-7 h-9 w-[260px]"
            data-testid="car-parts-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="car-parts-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate</SelectItem>
            <SelectItem value="ok">OK</SelectItem>
            <SelectItem value="low">Stoc scăzut</SelectItem>
            <SelectItem value="defect">Defect</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/inventar?tab=car-parts&car=${car.id}`)}
          data-testid="car-parts-open-inventar"
          className="border-blue-300 text-blue-700 hover:bg-blue-50"
        >
          <ExternalLink size={12} className="mr-1.5"/>
          Vezi în Inventar
        </Button>
        <div className="ml-auto">
          {canEdit && (
            <Button
              onClick={() => setDialog({ mode: "add", prefill: { item_type: "piesa_auto", linked_car_id: car.id } })}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="car-parts-add-btn"
            >
              <Plus size={14} className="mr-1.5"/>
              Adaugă piesă
            </Button>
          )}
        </div>
      </div>

      {/* Parts table */}
      <div className="card-flat overflow-hidden">
        <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 font-display font-bold text-sm uppercase tracking-wider text-emerald-900 flex items-center gap-2">
          <Wrench size={14}/>
          Piese & consumabile pe stoc pentru această mașină
        </div>
        {loading ? (
          <div className="p-8 text-center text-zinc-500">Se încarcă...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 italic" data-testid="car-parts-empty">
            {parts.length === 0
              ? <>Nicio piesă asociată acestei mașini. Apasă <strong>„Adaugă piesă&rdquo;</strong> pentru a începe.</>
              : "Nicio piesă nu corespunde filtrelor curente."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="px-3 py-2">Piesă</th>
                  <th className="px-3 py-2">Categorie</th>
                  <th className="px-3 py-2 text-right">Cantitate</th>
                  <th className="px-3 py-2">Locație</th>
                  <th className="px-3 py-2">Furnizor</th>
                  <th className="px-3 py-2 text-right">Preț CHF</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const qty = Number(p.quantity) || 0;
                  const lowStock = qty <= 1;
                  const isDefect = p.status === "defect";
                  return (
                    <tr
                      key={p.id}
                      className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                      onClick={() => canEdit && setDialog({ mode: "edit", item: p })}
                      data-testid={`car-parts-row-${p.id}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{p.name}</div>
                        {(p.serial || p.no_serial) && (
                          <div className="text-[10px] text-zinc-500 mt-0.5">
                            {p.serial ? <span className="font-mono">SN: {p.serial}</span> : <span className="italic">fără serie</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-zinc-600">
                        {p.equipment_category || <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        <span className={lowStock ? "text-amber-700 font-bold" : "text-zinc-800"}>
                          {qty}{p.unit ? ` ${p.unit}` : ""}
                        </span>
                        {lowStock && <AlertTriangle size={11} className="inline ml-1 text-amber-600"/>}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-zinc-600">
                        {p.location ? `📍 ${p.location}` : <span className="text-zinc-400">—</span>}
                        {p.shelf_row && p.shelf_col && <span className="text-zinc-400"> · raft {p.shelf_row}{p.shelf_col}</span>}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-zinc-600">
                        {p.supplier || <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-[11px] font-mono">
                        {p.price_chf != null ? <span className="text-emerald-700 font-bold">{Number(p.price_chf).toFixed(2)}</span> : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        {isDefect ? (
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-800">Defect</span>
                        ) : lowStock ? (
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Stoc scăzut</span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">OK</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {canEdit && (
                          <button
                            type="button"
                            className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-0.5"
                            onClick={(e) => { e.stopPropagation(); setDialog({ mode: "edit", item: p }); }}
                            data-testid={`car-parts-edit-${p.id}`}
                          >
                            <Edit size={11}/> Editează
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* History timeline */}
      <div className="card-flat overflow-hidden" data-testid="car-parts-history-section">
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 font-display font-bold text-sm uppercase tracking-wider text-blue-900 flex items-center gap-2">
          <History size={14}/>
          Istoric piese consumate la reparații & revizii
          {histKpi.totalItems > 0 && (
            <span className="ml-auto text-[11px] font-normal normal-case text-blue-700">
              {history.length} {history.length === 1 ? "intervenție" : "intervenții"} · {histKpi.totalItems} piese consumate total
            </span>
          )}
        </div>
        {history.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 italic text-sm">
            Nicio piesă consumată încă pentru această mașină.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {history.map((h) => (
              <div key={h.key} className="p-3 hover:bg-zinc-50 transition-colors" data-testid={`car-parts-hist-${h.key}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-600 text-white">
                      {fmtRoDate(h.date)}
                    </span>
                    <span className="font-medium text-sm">{h.reason}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                    {h.user && <span>👤 {h.user}</span>}
                    <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">{h.total_items} {h.total_items === 1 ? "piesă" : "piese"}</span>
                  </div>
                </div>
                <div className="mt-2 pl-2 border-l-2 border-blue-300 space-y-1">
                  {h.items.map((it, idx) => (
                    <div key={`${h.key}-${it.item_id}-${idx}`} className="flex items-center gap-2 text-[12px] text-zinc-700">
                      <span className="text-zinc-400">•</span>
                      <span className="flex-1">{it.name}</span>
                      <span className="font-mono tabular-nums text-zinc-600">× {it.qty}</span>
                      {it.from_location && <span className="text-[10px] text-zinc-400">📍 {it.from_location}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ItemDialog */}
      {dialog && meta && (
        <ItemDialog
          mode={dialog.mode}
          item={dialog.item}
          prefill={dialog.prefill}
          meta={meta}
          stores={[]}
          cars={cars}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, sub, color = "zinc", testid }) {
  const palette = {
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-900",
    blue: "border-blue-200 bg-blue-50/60 text-blue-900",
    zinc: "border-zinc-200 bg-zinc-50 text-zinc-800",
    amber: "border-amber-200 bg-amber-50/60 text-amber-900",
    red: "border-red-200 bg-red-50/60 text-red-900",
  }[color] || "border-zinc-200 bg-zinc-50 text-zinc-800";
  return (
    <div className={`rounded-lg border p-3 ${palette}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        {icon}
        {label}
      </div>
      <div className="font-display text-2xl font-extrabold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function ConsumptionReportCard({ consumption, months, onMonthsChange }) {
  if (!consumption) return null;
  const { months: monthKeys = [], categories = [], total_events = 0 } = consumption;

  const MONTH_RO = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const fmtMonth = (ym) => {
    const [y, m] = ym.split("-");
    return `${MONTH_RO[parseInt(m) - 1]} ${y.slice(2)}`;
  };

  const maxVal = Math.max(0.01, ...categories.flatMap((c) => Object.values(c.by_month || {})));

  return (
    <div className="card-flat overflow-hidden" data-testid="car-consumption-report">
      <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-200 flex items-center justify-between gap-2 flex-wrap">
        <div className="font-display font-bold text-sm uppercase tracking-wider text-indigo-900 flex items-center gap-2">
          <BarChart3 size={14}/>
          Consum lunar (ulei, antigel, filtre, piese)
          <span className="text-[11px] font-normal text-indigo-700/80">· {total_events} {total_events === 1 ? "eveniment" : "evenimente"}</span>
        </div>
        <div className="flex items-center gap-1">
          {[3, 6, 12].map((n) => (
            <button
              key={n}
              onClick={() => onMonthsChange(n)}
              className={`px-2.5 py-1 text-xs rounded font-semibold transition-colors ${months === n ? "bg-indigo-600 text-white" : "bg-white text-indigo-700 border border-indigo-300 hover:bg-indigo-100"}`}
              data-testid={`consumption-months-${n}`}
            >
              {n} luni
            </button>
          ))}
        </div>
      </div>
      {categories.length === 0 ? (
        <div className="p-6 text-center text-sm text-zinc-500 italic" data-testid="consumption-empty">
          Nu există consum înregistrat pentru această mașină în ultimele {months} luni.
          <div className="text-[11px] mt-1 opacity-70">Consumul apare automat după reparații/revizii cu piese folosite.</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="px-3 py-2 sticky left-0 bg-zinc-50">Categorie</th>
                {monthKeys.map((mk) => (
                  <th key={mk} className="px-2 py-2 text-center font-bold tabular-nums">{fmtMonth(mk)}</th>
                ))}
                <th className="px-3 py-2 text-right font-bold bg-indigo-50/60">Total</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.key} className="border-t border-zinc-100" data-testid={`consumption-row-${c.key}`}>
                  <td className="px-3 py-2 font-medium whitespace-nowrap sticky left-0 bg-white">{c.label}</td>
                  {monthKeys.map((mk) => {
                    const v = c.by_month?.[mk] || 0;
                    const intensity = v > 0 ? Math.min(1, v / maxVal) : 0;
                    const bg = v > 0 ? `rgba(99, 102, 241, ${0.08 + intensity * 0.45})` : "transparent";
                    return (
                      <td key={mk} className="px-2 py-2 text-center tabular-nums" style={{ backgroundColor: bg }}>
                        {v > 0 ? (
                          <span className="font-mono text-xs">{v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}</span>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right font-bold tabular-nums bg-indigo-50/40">
                    {c.total % 1 === 0 ? c.total.toFixed(0) : c.total.toFixed(2)}
                    <span className="text-[10px] text-zinc-500 ml-1">{c.unit}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

